import { useEffect, useState } from 'react';
import { Alert, StatCard } from '@/design-system';
import { ApiError } from '@/lib/api';
import { type Build, fetchBuild } from '@/lib/build';
import styles from './HomePage.module.css';

type State =
  | { kind: 'loading' }
  | { kind: 'ready'; build: Build }
  | { kind: 'failed'; message: string; requestId: string | undefined };

/**
 * The first page inside the application. It proves the browser reaches the API
 * and the API reaches the database, which is all a new build has on its first
 * day. The build's first feature replaces it.
 */
export function HomePage() {
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    fetchBuild()
      .then((build) => {
        if (!cancelled) setState({ kind: 'ready', build });
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setState({
            kind: 'failed',
            message:
              error instanceof Error ? error.message : 'The build endpoint could not be reached.',
            requestId: error instanceof ApiError ? error.requestId : undefined,
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className={styles.page}>
      <div className={styles.intro}>
        <h1 className={styles.title}>Home</h1>
        <p className={styles.lead}>
          This build is running. Replace this page with its first feature.
        </p>
      </div>

      {state.kind === 'loading' && <p className={styles.muted}>Reading the build marker.</p>}

      {state.kind === 'failed' && (
        <Alert tone="danger" title="The build marker could not be read">
          {state.message}
          {state.requestId && <> Request id: <span className="numeric">{state.requestId}</span>.</>}
        </Alert>
      )}

      {state.kind === 'ready' && (
        <section aria-labelledby="deployed">
          <h2 id="deployed" className={styles.sectionTitle}>
            What is deployed
          </h2>
          <div className={styles.stats}>
            <StatCard label="Version" value={state.build.version} />
            <StatCard label="Slice" value={state.build.slice} />
            <StatCard label="Commit" value={state.build.commit} />
          </div>
          <p className={styles.muted}>{state.build.description}</p>
        </section>
      )}
    </div>
  );
}
